/**
 * Кастомный хук для управления состоянием и логикой создания заказа
 *
 * Инкапсулирует:
 * - Управление шагами (source -> fields -> dishes)
 * - Загрузку меню и сотрудников
 * - Управление выбранными блюдами
 * - Расчёт цен с учётом скидок
 */
import { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { EmployeeStore } from '../../stores/EmployeeStore';
import { Employee } from '../../types/employee.types';
import { menuService } from '../../services/menu.service';
import { orderService } from '../../services/order.service';
import { MenuBySourceResponse, DishMenuItem, DishPortion } from '../../types/menu.types';
import { CreateOrderDto, OrderFormField } from '../../types/order.types';
import { SOURCE_FIELDS, ORDER_SOURCES } from '../../config/sourceFields';
import { OrderStep, OrderFormData, SelectedDishItem } from './types';

// Создаём экземпляр стора сотрудников
const employeeStore = new EmployeeStore();

export const useAddOrder = () => {
  const { t } = useLanguage();
  const { error: showError, success: showSuccess } = useNotification();

  // ============ Основные состояния ============

  /** Текущий шаг */
  const [step, setStep] = useState<OrderStep>('source');

  /** Выбранный источник заказа */
  const [selectedSource, setSelectedSource] = useState<string>('');

  /** Данные формы */
  const [formData, setFormData] = useState<OrderFormData>({});

  /** Список сотрудников для select */
  const [employees, setEmployees] = useState<Employee[]>([]);

  /** Данные меню */
  const [menuData, setMenuData] = useState<MenuBySourceResponse | null>(null);

  /** Флаг загрузки меню */
  const [loadingMenu, setLoadingMenu] = useState(false);

  /** Выбранные блюда */
  const [selectedDishes, setSelectedDishes] = useState<SelectedDishItem[]>([]);

  /** Модальные окна */
  const [selectedDishForModal, setSelectedDishForModal] = useState<DishMenuItem | null>(null);
  const [isDishModalOpen, setIsDishModalOpen] = useState(false);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);

  // ============ Эффекты ============

  /**
   * Загрузка списка сотрудников при выборе источника "employee"
   */
  useEffect(() => {
    if (selectedSource === 'employee') {
      employeeStore.loadEmployees().then(() => {
        setEmployees(employeeStore.activeEmployees);
      });
    }
  }, [selectedSource]);

  // ============ Навигация между шагами ============

  /**
   * Выбор источника заказа (шаг 1 -> шаг 2)
   */
  const handleSourceSelect = useCallback((source: string) => {
    setSelectedSource(source);
    setFormData({});
    setStep('fields');
  }, []);

  /**
   * Возврат к выбору источника
   */
  const handleBackToSource = useCallback(() => {
    setStep('source');
    setSelectedSource('');
    setFormData({});
  }, []);

  /**
   * Возврат к форме полей
   */
  const handleBackToFields = useCallback(() => {
    setStep('fields');
    setMenuData(null);
    setSelectedDishes([]);
  }, []);

  // ============ Работа с формой ============

  /**
   * Изменение значения поля формы
   */
  const handleFieldChange = useCallback((fieldName: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  }, []);

  /**
   * Загрузка меню и переход к выбору блюд (шаг 2 -> шаг 3)
   */
  const loadMenu = useCallback(async () => {
    setLoadingMenu(true);
    try {
      const promoCode = formData.promo_code as string | undefined;
      const menu = await menuService.getMenuBySource(selectedSource, promoCode);
      setMenuData(menu);
      setStep('dishes');
    } catch (err) {
      console.error('Failed to load menu:', err);
      showError(t('addOrder.errorLoadMenu'));
    } finally {
      setLoadingMenu(false);
    }
  }, [formData.promo_code, selectedSource, showError, t]);

  // ============ Работа с блюдами ============

  /**
   * Открытие модалки выбора блюда
   */
  const handleDishClick = useCallback((dish: DishMenuItem) => {
    setSelectedDishForModal(dish);
    setIsDishModalOpen(true);
  }, []);

  /**
   * Закрытие модалки выбора блюда
   */
  const handleCloseDishModal = useCallback(() => {
    setIsDishModalOpen(false);
    setSelectedDishForModal(null);
  }, []);

  /**
   * Обновление порций блюда (заменяет все порции блюда новыми значениями)
   * Используется модалкой выбора порций
   */
  const handleUpdateDishPortions = useCallback(
    (
      dish: DishMenuItem,
      portions: { portion: DishPortion; quantity: number; itemDiscountPercent?: number }[]
    ) => {
      setSelectedDishes((prev) => {
        // Удаляем все существующие порции этого блюда
        const withoutCurrentDish = prev.filter((item) => item.dishId !== dish.id);

        // Добавляем новые порции
        const newPortions: SelectedDishItem[] = portions.map(
          ({ portion, quantity, itemDiscountPercent }) => ({
            dishId: dish.id,
            dishName: dish.name,
            portionId: portion.id,
            weight: portion.weight,
            price: portion.price,
            quantity,
            allowPromo: portion.allow_promo,
            allowItemDiscount: portion.allow_item_discount,
            itemDiscountPercent,
          })
        );

        return [...withoutCurrentDish, ...newPortions];
      });
    },
    []
  );

  /**
   * Удаление блюда из заказа
   */
  const handleRemoveDish = useCallback((dishId: number, portionId: number) => {
    setSelectedDishes((prev) =>
      prev.filter((item) => !(item.dishId === dishId && item.portionId === portionId))
    );
  }, []);

  /**
   * Обновление количества блюда
   * Если количество <= 0, удаляет блюдо
   */
  const handleUpdateQuantity = useCallback(
    (dishId: number, portionId: number, quantity: number) => {
      if (quantity <= 0) {
        handleRemoveDish(dishId, portionId);
        return;
      }

      setSelectedDishes((prev) =>
        prev.map((item) =>
          item.dishId === dishId && item.portionId === portionId
            ? { ...item, quantity }
            : item
        )
      );
    },
    [handleRemoveDish]
  );

  // ============ Расчёт цен ============

  /**
   * Расчёт финальной цены порции с учётом всех скидок
   * Порядок применения: сначала ручная скидка, потом промокод
   */
  const calculateFinalPrice = useCallback(
    (item: SelectedDishItem): number => {
      let price = item.price;

      // Применяем ручную скидку (если задана)
      if (item.allowItemDiscount && item.itemDiscountPercent && item.itemDiscountPercent > 0) {
        price = price * (1 - item.itemDiscountPercent / 100);
      }

      // Применяем скидку промокода
      if (menuData?.promo.is_valid && menuData.promo.discount_percent && item.allowPromo) {
        price = price * (1 - menuData.promo.discount_percent / 100);
      }

      return price;
    },
    [menuData]
  );

  /**
   * Расчёт общей суммы заказа
   */
  const calculateTotal = useCallback(() => {
    return selectedDishes.reduce((sum, item) => {
      return sum + calculateFinalPrice(item) * item.quantity;
    }, 0);
  }, [selectedDishes, calculateFinalPrice]);

  // ============ Создание заказа ============

  /** Флаг отправки заказа */
  const [creatingOrder, setCreatingOrder] = useState(false);

  /**
   * Формирование form_data с метаданными для сохранения
   */
  const buildFormDataWithMetadata = useCallback((): Record<string, OrderFormField> => {
    const sourceConfig = SOURCE_FIELDS[selectedSource];
    if (!sourceConfig) return {};

    const result: Record<string, OrderFormField> = {};

    for (const field of sourceConfig.fields) {
      const value = formData[field.name];
      if (value === undefined || value === '') continue;

      // Проверяем showIf условие
      if (field.showIf) {
        const conditionValue = formData[field.showIf.field];
        if (conditionValue !== field.showIf.value) continue;
      }

      const fieldData: OrderFormField = {
        value,
        label_ru: field.label_ru,
        label_en: field.label_en,
      };

      // Для buttons и select добавляем лейблы значений
      if (field.options && typeof value === 'string') {
        const option = field.options.find((opt) => opt.value === value);
        if (option) {
          fieldData.value_label_ru = option.label_ru;
          fieldData.value_label_en = option.label_en;
        }
      }

      // Для select с loadOptions (сотрудники) ищем в списке
      if (field.type === 'select' && field.loadOptions && typeof value === 'string') {
        const employee = employees.find((emp) => emp.id.toString() === value);
        if (employee) {
          fieldData.value_label_ru = employee.name;
          fieldData.value_label_en = employee.name;
        }
      }

      result[field.name] = fieldData;
    }

    return result;
  }, [selectedSource, formData, employees]);

  /**
   * Создание заказа
   */
  const handleCreateOrder = useCallback(async () => {
    if (selectedDishes.length === 0) {
      showError(t('addOrder.noItemsSelected'));
      return;
    }

    if (!menuData) {
      showError(t('addOrder.errorLoadMenu'));
      return;
    }

    setCreatingOrder(true);

    try {
      // Находим информацию об источнике
      const sourceInfo = ORDER_SOURCES.find((s) => s.value === selectedSource);

      // Формируем данные заказа
      const orderData: CreateOrderDto = {
        source_info: {
          code: selectedSource,
          name_ru: sourceInfo?.label_ru || selectedSource,
          name_en: sourceInfo?.label_en || selectedSource,
        },
        form_data: buildFormDataWithMetadata(),
        items: selectedDishes.map((item) => {
          // Находим блюдо чтобы получить картинку
          const dish = menuData.dishes.find((d) => d.id === item.dishId);
          return {
            dish_id: item.dishId,
            dish_name: item.dishName,
            dish_image: dish?.image,
            portion_id: item.portionId,
            weight: item.weight,
            original_price: item.price,
            final_price: calculateFinalPrice(item),
            quantity: item.quantity,
            promo_applied: item.allowPromo && menuData.promo.is_valid,
            item_discount_percent: item.itemDiscountPercent,
          };
        }),
        subtotal: selectedDishes.reduce((sum, item) => sum + item.price * item.quantity, 0),
        total: calculateTotal(),
      };

      // Добавляем промокод если валидный
      if (menuData.promo.is_valid && menuData.promo.code && menuData.promo.promo_code_id) {
        orderData.promo_info = {
          promo_code_id: menuData.promo.promo_code_id,
          code: menuData.promo.code,
          discount_percent: menuData.promo.discount_percent || 0,
          is_valid: true,
        };
      }

      await orderService.createOrder(orderData);
      showSuccess(t('addOrder.orderCreated'));

      // Сбрасываем форму после успешного создания
      setStep('source');
      setSelectedSource('');
      setFormData({});
      setMenuData(null);
      setSelectedDishes([]);
      setIsOrderDetailsOpen(false);
    } catch (err) {
      console.error('Failed to create order:', err);
      showError(t('addOrder.errorCreateOrder'));
    } finally {
      setCreatingOrder(false);
    }
  }, [
    selectedDishes,
    selectedSource,
    menuData,
    buildFormDataWithMetadata,
    calculateFinalPrice,
    calculateTotal,
    showError,
    showSuccess,
    t,
  ]);

  // ============ Модальные окна ============

  const openOrderDetails = useCallback(() => {
    setIsOrderDetailsOpen(true);
  }, []);

  const closeOrderDetails = useCallback(() => {
    setIsOrderDetailsOpen(false);
  }, []);

  return {
    // Состояния
    step,
    selectedSource,
    formData,
    employees,
    menuData,
    loadingMenu,
    selectedDishes,
    selectedDishForModal,
    isDishModalOpen,
    isOrderDetailsOpen,

    // Навигация
    handleSourceSelect,
    handleBackToSource,
    handleBackToFields,

    // Форма
    handleFieldChange,
    loadMenu,

    // Блюда
    handleDishClick,
    handleCloseDishModal,
    handleUpdateDishPortions,
    handleRemoveDish,
    handleUpdateQuantity,

    // Расчёты
    calculateFinalPrice,
    calculateTotal,

    // Заказ
    handleCreateOrder,
    creatingOrder,

    // Модальные окна
    openOrderDetails,
    closeOrderDetails,
  };
};
